import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  FusionProvider,
  useCredentials,
  useBridgePort,
  useFetchFileMetadata,
  useFetchFileContent,
  useShouldUpdateApp,
  useRegisterApp,
  useIframeCredentials,
  useTrackAppLoading,
} from "./hooks";
import { processZipFile, type SourceCodeResult } from "./utils/fileUtils";
import type { AppRegistration } from "../sw/types";
import { stripProtocol } from "../sw/appCache";
import { LoadingScreen } from "./components";

/**
 * Component for the /dune route - handles Fusion integration
 * Wraps content with FusionProvider to receive credentials and bridge port
 */
export const AppWithCredentials: React.FC = () => {
  return (
    <FusionProvider>
      <AppWithCredentialsContent />
    </FusionProvider>
  );
};

/**
 * Inner component that consumes Fusion context
 */
const AppWithCredentialsContent: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const { credentials } = useCredentials();
  const bridgePort = useBridgePort();
  const [sourceCode, setSourceCode] = useState<SourceCodeResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<Error | null>(null);

  // Step 1: Fetch only file metadata (not content)
  const {
    fileMetadata,
    isLoading: isFetchingMetadata,
    error: fetchMetadataError,
  } = useFetchFileMetadata(appId, credentials);

  // lastUpdated is already a Unix timestamp in milliseconds
  const lastUpdatedTime = fileMetadata?.lastUpdated;

  // Step 2: Check if app should update - memoized to prevent infinite loop
  const updateCheck = useMemo(
    () =>
      credentials?.baseUrl &&
      credentials?.project &&
      appId &&
      lastUpdatedTime !== undefined
        ? {
            baseUrl: credentials.baseUrl,
            project: credentials.project,
            appId,
            lastUpdatedTime,
          }
        : undefined,
    [credentials?.baseUrl, credentials?.project, appId, lastUpdatedTime],
  );

  const {
    response: shouldUpdateResponse,
    isLoading: isCheckingUpdate,
    error: updateCheckError,
  } = useShouldUpdateApp(updateCheck);

  // Step 3: Only fetch file content if update is needed
  const shouldFetchContent = shouldUpdateResponse?.shouldUpdate === true;
  const {
    binaryData: fileContent,
    isLoading: isFetchingContent,
    error: fetchContentError,
  } = useFetchFileContent(appId, credentials, shouldFetchContent);

  // Register app after processing (only when sourceCode is available) - memoized to prevent infinite loop
  const registration: AppRegistration | undefined = useMemo(
    () =>
      credentials?.baseUrl &&
      credentials?.project &&
      appId &&
      lastUpdatedTime !== undefined &&
      sourceCode
        ? {
            baseUrl: credentials.baseUrl,
            project: credentials.project,
            appId,
            lastUpdatedTime,
            files: sourceCode,
          }
        : undefined,
    [
      credentials?.baseUrl,
      credentials?.project,
      appId,
      lastUpdatedTime,
      sourceCode,
    ],
  );

  const {
    response: registerResponse,
    isLoading: isRegistering,
    error: registerError,
  } = useRegisterApp(registration);

  const { iframeRef } = useIframeCredentials(credentials, window.location.origin, bridgePort);

  // Step 4: Process ZIP file only when fileContent is available AND update is needed
  useEffect(() => {
    let isActive = true;
    // Only process if we have file content (which is only fetched when update is needed)
    if (
      fileContent &&
      fileMetadata &&
      shouldUpdateResponse?.shouldUpdate === true
    ) {
      setIsProcessing(true);
      setProcessingError(null);

      processZipFile(fileContent, fileMetadata.fileName)
        .then((result) => {
          if (isActive) {
            setSourceCode(result);
          }
          setIsProcessing(false);
        })
        .catch((error) => {
          if (isActive) {
            setProcessingError(error);
          }
          setIsProcessing(false);
        });
    }

    return () => {
      isActive = false;
    };
  }, [fileContent, fileMetadata, shouldUpdateResponse?.shouldUpdate]);

  // Derive a friendly app name from metadata only (don't show appId)
  const appName = useMemo(() => {
    // Only show app name if we have the nice metadata name
    if (fileMetadata?.metadata?.name) {
      return fileMetadata.metadata.name;
    }
    // Otherwise return undefined to show generic "Loading app..."
    return undefined;
  }, [fileMetadata?.metadata]);

  // Check if there are any errors
  const hasError =
    fetchMetadataError ||
    fetchContentError ||
    processingError ||
    updateCheckError ||
    registerError;

  // App is ready when either:
  // 1. Already cached and up-to-date (no update needed)
  // 2. Successfully registered after processing update
  const isAppReady =
    (shouldUpdateResponse?.shouldUpdate === false && !isCheckingUpdate) ||
    (registerResponse?.success &&
      !isFetchingMetadata &&
      !isFetchingContent &&
      !isProcessing &&
      !isRegistering);

  // Check if we should show content (either loading, error, or ready)
  const shouldShowApp =
    isAppReady && appId && credentials?.baseUrl && credentials?.project;

  // Track app loading performance
  useTrackAppLoading({
    isAppReady,
    appId,
    appName: fileMetadata?.metadata?.name,
    shouldUpdate: shouldUpdateResponse?.shouldUpdate,
    fileSizeBytes: fileContent?.byteLength,
  });

  // Render the app iframe if ready
  if (shouldShowApp) {
    // App is cached and ready - load it from the service worker
    // URL format: /{baseUrl}/{project}/{appId}/appfiles/index.html
    const baseUrlWithoutProtocol = stripProtocol(credentials.baseUrl);
    const appUrl = `/${baseUrlWithoutProtocol}/${credentials.project}/${appId}/appfiles/index.html`;

    return (
      <iframe
        key={appId}
        ref={iframeRef}
        src={appUrl}
        style={{
          display: "block",
          width: "100%",
          height: "100vh",
          border: "none",
        }}
        title={`App ${appId}`}
        allow="accelerometer; autoplay; camera; clipboard-write; encrypted-media; fullscreen; geolocation; gyroscope; microphone; picture-in-picture"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads allow-fullscreen"
      />
    );
  }

  // Show error screen if there are errors
  if (hasError) {
    const errorMessage =
      fetchMetadataError?.message ||
      fetchContentError?.message ||
      processingError?.message ||
      updateCheckError?.message ||
      registerError?.message;

    return (
      <LoadingScreen
        type="error"
        appName={appName}
        errorMessage={errorMessage}
      />
    );
  }

  // Show loading screen for all other states (keeps component mounted)
  return <LoadingScreen type="loading" appName={appName} />;
};

export default AppWithCredentials;
